import React, { useState, useEffect } from 'react';
import {
  Button,
  Form,
  Container,
  Card,
  Spinner,
  Toast,
  ToastContainer,
} from 'react-bootstrap';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  FaEnvelope,
  FaLock,
  FaSignInAlt,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaEye,
  FaEyeSlash,
} from 'react-icons/fa';
import BASE_URL from '../config';
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [role, setRole] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const showToast = (message, type = 'info') => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ ...toast, show: false });
  };

  useEffect(() => {
    document.body.style.backgroundColor = '';
    document.body.classList.add('login-page');
    return () => {
      document.body.classList.remove('login-page');
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    hideToast();

    try {
      // 1. Initial Login Request
      const loginResponse = await axios.post(`${BASE_URL}/login`, {
        email,
        password,
      });

      if (loginResponse.data.success) {
        const { token, user } = loginResponse.data.data;
        const userRole = user.role;
        const userName = user.name;
        const userId = user.id; // Get user ID

        // Store essential login data immediately
        localStorage.setItem('authToken', token);
        localStorage.setItem('userRole', userRole);
        localStorage.setItem('userId', userId);
        localStorage.setItem('userName', userName);

        let branchName = 'No Branch Assigned';
        let userShowroomId = '';

        // --- START NEW TRY/CATCH BLOCK for fetching details ---
        try {
          // 2. Fetch User Details to get Showroom Data (using the authenticated /api/users/{id} endpoint)
          console.log(
            `Attempting to fetch user details for ID: ${userId} at URL: ${BASE_URL}/users/${userId}`
          );

          const userDetailResponse = await axios.get(
            `${BASE_URL}/users/${userId}`,
            {
              headers: {
                // CRITICAL: Must use the token received from the login response
                Authorization: `Bearer ${token}`,
              },
            }
          );

          // --- SUCCESS PATH ---
          const userDetails = userDetailResponse.data.data;
          const showroomDetails = Array.isArray(userDetails.showrooms)
            ? userDetails.showrooms
            : [];

          const primaryShowroom =
            showroomDetails.length > 0 ? showroomDetails[0] : null;
          userShowroomId = primaryShowroom ? primaryShowroom.id : null;
          branchName = primaryShowroom
            ? primaryShowroom.name
            : 'No Branch Assigned';

          console.log('Showroom API Response Structure:', userDetails.showroom);
          console.log('Branch name successfully extracted:', branchName);
        } catch (detailError) {
          // --- FAILURE PATH ---
          console.error('--- SECOND API CALL FAILED ---');
          if (detailError.response) {
            // Server responded with an error status (e.g., 401 Unauthorized)
            console.error('Failure Status Code:', detailError.response.status);
            console.error('Failure Response Data:', detailError.response.data);
          } else if (detailError.request) {
            // Request was made but no response was received (e.g., network error, CORS)
            console.error('Network Error: No response received from server.');
          } else {
            // Something else triggered the error
            console.error('Request setup error:', detailError.message);
          }
          // branchName remains "No Branch Assigned"
        }
        // --- END NEW TRY/CATCH BLOCK ---

        // 3. Store Showroom Data
        localStorage.setItem('userShowroomId', userShowroomId || '');
        localStorage.setItem('branchName', branchName);

        console.log('Login successful!');
        console.log('Token:', token);
        console.log('Role:', userRole);
        console.log('Name:', userName);
        console.log('User ID:', userId);
        console.log('Branch:', branchName);

        setRole(userRole);
        showToast(
          `Logged in successfully. Welcome back!`,
          'success'
        );

        // 4. Navigation
        setTimeout(() => {
          setIsLoading(false);
          if (userRole === 'admin' || userRole === 'logistics_manager') {
            navigate('/Dashboard');
          } else if (userRole === 'branch_manager') {
            navigate('/BranchManager');
          } else if (userRole === 'sales') {
            navigate('/SalesForm');
          } else if (userRole === 'accountant') {
            navigate('/PaymentPortal');
          } else if (userRole === 'cashier') {
            navigate('/CashierPortal');
          } else {
            navigate('/');
          }
        }, 1500);
      } else {
        // Handle unsuccessful login response
        showToast(
          loginResponse.data.message ||
            'Invalid credentials. Please try again.',
          'danger'
        );
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Login Error:', error.response?.data || error.message);

      if (error.response) {
        // Handle server responses with error status codes
        if (error.response.status === 401 || error.response.status === 400) {
          showToast('Invalid email or password. Please try again.', 'danger');
        } else if (error.response.status === 500) {
          showToast('Server error. Please try again later.', 'warning');
        } else {
          showToast(
            error.response.data.message ||
              'An error occurred. Please try again.',
            'warning'
          );
        }
      } else if (error.request) {
        // Handle network errors (no response received)
        showToast('Network error. Please check your connection.', 'warning');
      } else {
        // Handle other errors (request setup issues)
        showToast('An unexpected error occurred. Please try again.', 'warning');
      }
    }
  };

  return (
    <div className='login-split-container'>
      {/* Left Side - Login Form */}
      <div className='login-form-section'>
        <div className='login-form-wrapper'>
          <div className='login-header'>
            
            <div className='d-flex align-items-center'>
              <img
                src='/sparemate_logo.png'
                alt='Sparemate'
                className='me-2'
                style={{ width: '30px', height: '30px' }}
              />
              <h1 className='login-brand-name'>SPAREMATE</h1>
            </div>
          </div>

          <Form onSubmit={handleSubmit} className='login-form'>
            <Form.Group className='mb-3 form-group-custom'>
              <Form.Label className='form-label-custom'>Username</Form.Label>
              <Form.Control
                type='email'
                placeholder='Johnsmith001@gmail.com'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className='form-control-custom'
                disabled={isLoading}
              />
            </Form.Group>

            <Form.Group className='mb-3 form-group-custom'>
              <Form.Label className='form-label-custom'>Password</Form.Label>
              <div className='password-input-wrapper'>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  placeholder='••••••••'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className='form-control-custom'
                  disabled={isLoading}
                />
                <Button
                  type='button'
                  variant='link'
                  className='password-toggle'
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <FaEyeSlash className='eye-icon' />
                  ) : (
                    <FaEye className='eye-icon' />
                  )}
                </Button>
              </div>
            </Form.Group>

            <Button
              type='submit'
              variant='primary'
              className='login-button'
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Spinner animation='border' size='sm' className='me-2' />
                  Signing in...
                </>
              ) : (
                'LOGIN'
              )}
            </Button>
          </Form>
        </div>
      </div>

      {/* Right Side - Illustration */}
      <div className='login-illustration-section'>
        <div className='illustration-background'>
          <div className='abstract-shape shape-1'></div>
          <div className='abstract-shape shape-2'></div>
          <div className='abstract-shape shape-3'></div>
          <div className='abstract-shape shape-4'></div>
          <div className='abstract-shape shape-5'></div>
          <div className='abstract-shape shape-6'></div>
          {/* Small dots */}
          <div className='small-dot dot-1'></div>
          <div className='small-dot dot-2'></div>
          <div className='small-dot dot-3'></div>
          <div className='small-dot dot-4'></div>
          <div className='small-dot dot-5'></div>
          <div className='small-dot dot-6'></div>
          <div className='small-dot dot-7'></div>
          <div className='small-dot dot-8'></div>
          <div className='small-dot dot-9'></div>
          <div className='small-dot dot-10'></div>
          {/* Medium dots */}
          <div className='medium-dot medium-dot-1'></div>
          <div className='medium-dot medium-dot-2'></div>
          <div className='medium-dot medium-dot-3'></div>
          <div className='medium-dot medium-dot-4'></div>
          <div className='medium-dot medium-dot-5'></div>
          <div className='medium-dot medium-dot-6'></div>
          <div className='medium-dot medium-dot-7'></div>
          <div className='medium-dot medium-dot-8'></div>
        </div>
      </div>

      {/* Toast Container */}
      <ToastContainer
        position='top-end'
        className='p-3'
        style={{ zIndex: 9999 }}
      >
        <Toast
          show={toast.show}
          onClose={hideToast}
          delay={5000}
          autohide
          bg={
            toast.type === 'success'
              ? 'success'
              : toast.type === 'danger'
              ? 'danger'
              : 'warning'
          }
        >
          <Toast.Header>
            {toast.type === 'success' && (
              <FaCheckCircle className='me-2 text-success' />
            )}
            {toast.type === 'danger' && (
              <FaTimesCircle className='me-2 text-danger' />
            )}
            {(toast.type === 'warning' || toast.type === 'info') && (
              <FaExclamationTriangle className='me-2 text-warning' />
            )}
            <strong className='me-auto'>
              {toast.type === 'success'
                ? 'Success'
                : toast.type === 'danger'
                ? 'Error'
                : 'Notice'}
            </strong>
          </Toast.Header>
          <Toast.Body className='text-white'>{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default Login;
